import React, { useEffect, useState } from 'react';
import { Checkbox, Divider, InputNumber} from 'ui';
import { Form, FormInstance, Spin } from 'antd';
import { useIntl } from 'react-intl';
import useQueryApiClient from 'utils/useQueryApiClient';
import { WithoutModalAddressFinder } from 'ui/AdressSearch/WithoutModal';
import { StyledFarmSearchFilterSettings } from './style';
import type {
  FarmSearchFilterMap,
  FarmSearchFilterProps,
} from './types';

interface FarmSearchFilterSettingsComponentProps extends FarmSearchFilterProps {
  form: FormInstance<FarmSearchFilterMap>;
  onSavingChange?: (saving: boolean) => void;
}

function FarmSearchFilterSettings({
  form,
  onSaved,
  onDirtyChange,
  onSavingChange,
}: FarmSearchFilterSettingsComponentProps) {
  const intl = useIntl();

  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const productsEnabled = Form.useWatch('products', form);
  const distanceValue = Form.useWatch('distance', form);

  const { refetch: fetchSettings, isLoading: settingsLoading } = useQueryApiClient({
    request: { url: '/api/v2/notification-preferences/farm-search-filter', disableOnMount: true },
    onSuccess: (resp: { data?: FarmSearchFilterMap }) => {
      form.setFieldsValue(resp?.data ?? {
        products: false,
        quality_marks: false,
        distance: null,
        notification_to_farm_email: false,
        address: null,
        lks_lat: null,
        lks_long: null,
      });
      setInitialLoadComplete(true);
    },
  });

  useEffect(() => {
    const defaultValues: FarmSearchFilterMap = {
      products: false,
      quality_marks: false,
      distance: null,
      notification_to_farm_email: false,
      address: null,
      lks_lat: null,
      lks_long: null,
    };
    form.setFieldsValue(defaultValues);
    fetchSettings?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { appendData: putSettings, isLoading: saving } = useQueryApiClient({
    request: { url: '/api/v2/notification-preferences/farm-search-filter', method: 'PUT' },
    onSuccess: (resp: { data?: FarmSearchFilterMap }) => {
      const data: FarmSearchFilterMap = resp?.data ?? form.getFieldsValue();
      form.setFields(
        (Object.keys(data) as (keyof FarmSearchFilterMap)[]).map((name) => ({
          name,
          value: data[name],
          touched: false,
        }))
      );
      onDirtyChange?.(false);
      onSaved?.(data);
    },
  });

  const handleSubmit = (values: FarmSearchFilterMap) => {
    putSettings(values);
  };

  const handleFieldsChange = () => {
    const isDirty = form.isFieldsTouched();
    onDirtyChange?.(isDirty);
  };

  useEffect(() => {
    onSavingChange?.(saving);
  }, [saving, onSavingChange]);

  useEffect(() => {
    if (!productsEnabled) {
      const { quality_marks, distance, notification_to_farm_email, address, lks_lat, lks_long } = form.getFieldsValue();
      if (quality_marks || distance != null || notification_to_farm_email || address != null || lks_lat != null || lks_long != null) {
        form.setFieldsValue({
          quality_marks: false,
          distance: null,
          notification_to_farm_email: false,
          address: null,
          lks_lat: null,
          lks_long: null,
        });
      }
    }
  }, [productsEnabled, form]);

  useEffect(() => {
    if (!distanceValue) {
      const { address, lks_lat, lks_long } = form.getFieldsValue();
      if (address != null || lks_lat != null || lks_long != null) {
        form.setFieldsValue({
          address: null,
          lks_lat: null,
          lks_long: null,
        });
      }
    }
  }, [distanceValue, form]);

  if (settingsLoading || !initialLoadComplete) {
    return (
      <StyledFarmSearchFilterSettings>
        <div className="fsf-loading">
          <Spin />
        </div>
      </StyledFarmSearchFilterSettings>
    );
  }

  return (
    <StyledFarmSearchFilterSettings>
      <Form
        form={form}
        onFinish={handleSubmit}
        onFieldsChange={handleFieldsChange}
        layout="vertical"
      >
        <div className="fsf-content">
          <div className="fsf-section">
            <h3>
              {intl.formatMessage({ id: 'farm.search_filter.notification_settings' })}
            </h3>
            <p className="description">
              {intl.formatMessage({ id: 'farm.search_filter.description' })}
            </p>

            <Divider />

            <div className="fsf-fields">
              <Checkbox
                name="products"
                label={intl.formatMessage({ id: 'farm.search_filter.products' })}
              />

              <Checkbox
                name="quality_marks"
                label={intl.formatMessage({ id: 'farm.search_filter.quality_marks' })}
                disabled={!productsEnabled}
              />

              <Checkbox
                name="notification_to_farm_email"
                label={intl.formatMessage({ id: 'farm.search_filter.notification_to_farm_email' })}
                disabled={!productsEnabled}
              />

              <div className="distance-field">
                <label>{intl.formatMessage({ id: 'farm.search_filter.distance' })}</label>

                <InputNumber
                  name="distance"
                  min={0}
                  max={500}
                  suffix="Km"
                  placeholder={intl.formatMessage({
                    id: 'farm.search_filter.distance_placeholder',
                  })}
                  disabled={!productsEnabled}
                />

                <span className="help-text">
                  {intl.formatMessage({ id: 'farm.search_filter.distance_help' })}
                </span>
              </div>

              <div className="address-field">
                <WithoutModalAddressFinder
                  form={form}
                  name="address"
                  disabled={!productsEnabled || !distanceValue}
                  label={intl.formatMessage({ id: 'farm.search_filter.address' })}
                  requireCoordinates
                  validations={distanceValue ? 'required' : undefined}
                  placeholder=""
                />
              </div>
            </div>
          </div>
        </div>
      </Form>
    </StyledFarmSearchFilterSettings>
  );
}

export default FarmSearchFilterSettings;
export type {
  FarmSearchFilterMap,
  FarmSearchFilterProps,
} from './types';